import ReactSelect, { components } from 'react-select';
import styled from 'styled-components';
import { theme } from '../theme';
import { FormItem, ValidationState } from '../Form/FormItem';
import React, { useMemo } from 'react';
import { SvgIcon } from '@salecycle/ui-component-library';

const StyledSelect = styled(ReactSelect)`
  ${props => props.theme.partials.newForm.item.default};
`;

const ItemWithChildren = styled.div<{ catergoryLevel: number }>`
  display: flex;
  align-items: center;
  padding-left: ${({ catergoryLevel }) => (catergoryLevel === 1 ? '0' : theme.designToken.spacing4)};
  strong {
    font: ${({ theme }) => theme.designToken.body2Semibold};
  }
`;

const ItemWithoutChildren = styled.div<{ catergoryLevel: number }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: ${({ theme, catergoryLevel }) =>
    catergoryLevel === 2 ? theme.designToken.spacing5 : theme.designToken.spacing7};
  span {
    font: ${({ theme }) => theme.designToken.body2};
  }
`;

const SelecetedItem = styled.div`
  font: ${({ theme }) => theme.designToken.body2};
  overflow: hidden;
  color: ${({ theme }) => theme.designToken.neutral900};
  text-overflow: ellipsis;
`;

const IconContainer = styled.div`
  display: block;
  padding-right: ${({ theme }) => theme.designToken.spacing2};
`;

export const ReactSelectStyles = {
  container: styles => ({
    ...styles,
    padding: 0,
    margin: 0,
  }),
  control: (styles, { isDisabled }) => ({
    ...styles,
    color: isDisabled ? theme.vars.newColor.darkGS3 : null,
    background: isDisabled ? theme.vars.newColor.lightGS2 : theme.vars.newColor.white,
    border: 'none',
    boxShadow: 'none',
    minHeight: '48px',
    padding: `${theme.designToken.spacing2} ${theme.designToken.spacing3}`,
    width: '100%',

    ':hover': {
      boxShadow: 'none',
    },
  }),
  valueContainer: styles => ({
    ...styles,
    padding: `0 ${theme.designToken.spacing2} 0 0`,
  }),
  singleValue: styles => ({
    ...styles,
    margin: 0,
    padding: 0,

    '> div': {
      display: 'none',
    },
    '> span': {
      margin: '0 !important',
    },
  }),
  placeholder: styles => ({
    ...styles,
    color: theme.vars.newColor.lightGS1,
  }),
  input: styles => ({
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (styles, state) => ({
    ...styles,
    padding: 0,
    font: theme.designToken.body2,
    color: theme.designToken.neutral700,
    transform: state.selectProps.menuIsOpen && 'rotate(180deg)',
  }),
  menu: styles => ({
    ...styles,
    zIndex: 2,
    borderRadius: '0',
    boxShadow: 'none',
    margin: `${theme.designToken.spacing1} 0`,
    border: `1px solid ${theme.vars.newColor.lightGS1}`,
  }),
  menuList: styles => ({
    ...styles,
    padding: '0',
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? theme.designToken.primary700 : isFocused ? theme.designToken.primary50 : null,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: '375',
    padding: `${theme.designToken.spacing2} ${theme.designToken.spacing3}`,

    path: {
      fill: isSelected ? 'white' : null,
    },
  }),
};

export interface ISelectOptionWithChildren {
  value: string;
  label: string;
  isOpen?: boolean;
  children: ISelectOptionWithChildren[];
  categoryLevel?: number; // this level of option field is automatically set within the component
}

export type MenuPlacement = 'auto' | 'bottom' | 'below' | 'top' | 'above';

export interface IProps {
  /** Select label */
  label?: string;
  /** ARIA label tag */
  ariaLabel: string;
  /** Will be passed through to the labels for attribute */
  inputId?: string;
  /**
   * The placeholder to display when no value is specified. Defaults to "Choose
   * a value".
   */
  placeholder?: string;
  /** The selected item. Undefined by default. */
  value?: ISelectOptionWithChildren;
  /** Item to be selected on first load. Undefined by default. */
  defaultValue?: ISelectOptionWithChildren;
  /** The list of items to select from. {value:string, label:string}. */
  options?: Array<ISelectOptionWithChildren>;
  /** Event invoked when a new value is selected. */
  onChange?: (value: ISelectOptionWithChildren) => void;
  /** Event invoked when a new item is created. */
  onCreateOption?: (inputValue: string) => void;
  /** Input validation state (complete, warning).  */
  validationState?: ValidationState;
  /** Input validation message.  */
  validationMessage?: string;
  /** Adds spacing below the component (default: true). */
  spacingBelow?: boolean;
  /** A hint can be passed as HTML to explain what the select is used for. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hint?: any;
  /** Optional tooltip heading.  Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description.  Defaults to undefined. */
  tooltipDescription?: string;
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  /**
   * The placement of the menu. One of "auto", "bottom", or "top" (defaults to
   * "bottom").
   */
  menuPlacement?: MenuPlacement;
  /** Height of the menu in pixels. Defaults to 300. */
  menuHeight?: number;
  /** Whether to disable the select or not.  Defaults to false. */
  disabled?: boolean;
  /** Placeholder which appears when there are no options to display */
  noOptionsPlaceholder?: string;
  /** Whether or not the menu is open on load. Defaults to false. */
  defaultMenuOpen?: boolean;
  /** Whether or not to focus the menu on mount. Defaults to false. */
  autoFocus?: boolean;
  /**
   * react-select doesn't handle being inline very well. So we need to ensure
   * that when it's inline it sets FormItem to full width.
   */
  inline?: boolean;
}

export const SelectTree = ({
  ariaLabel,
  autoFocus,
  defaultMenuOpen,
  defaultValue,
  disabled,
  hint,
  inline,
  inputId,
  label,
  menuHeight,
  menuPlacement,
  noOptionsPlaceholder,
  onChange,
  onCreateOption,
  options: topLevelOptions,
  placeholder,
  spacingBelow,
  tooltipDescription,
  tooltipHeading,
  tooltipWidth,
  validationMessage,
  validationState,
  value,
}: IProps) => {
  const transformSelectedOption = (selectedOption: ISelectOptionWithChildren) => {
    return selectedOption ? selectedOption : null;
  };

  let selectMenuPlacement: MenuPlacement = menuPlacement;

  if (menuPlacement === 'above') {
    selectMenuPlacement = 'top';
  } else if (menuPlacement === 'below') {
    selectMenuPlacement = 'bottom';
  }

  const [menuIsOpen, setMenuIsOpen] = React.useState<boolean>();

  const optionsWithLevels = useMemo(() => {
    topLevelOptions.forEach(p => {
      p.categoryLevel = 1;
      p.children?.forEach(s => {
        s.categoryLevel = 2;
        s.children?.forEach(t => {
          t.categoryLevel = 3;
        });
      });
    });
    return topLevelOptions;
  }, [topLevelOptions]);

  const [options, setOptions] = React.useState<ISelectOptionWithChildren[]>([...optionsWithLevels]);

  const [shouldRenderSingleValue, setShouldRenderSingleValue] = React.useState<boolean>(true);

  const onChangeHandler = (selectedOption: ISelectOptionWithChildren) => {
    const selectedOptionIsCategory = !!selectedOption.children?.length;

    if (selectedOptionIsCategory) {
      const indexOfSelectedOption = options.findIndex(p => p === selectedOption);
      const selectedCategoryIsClosed = !options.find(p => p.value === selectedOption.children[0].value);

      if (selectedCategoryIsClosed) {
        const childrenToAddIn = options.find(p => p.value === selectedOption.value).children;

        childrenToAddIn.forEach(child => {
          child.isOpen = false;
        });

        options.splice(indexOfSelectedOption + 1, 0, ...options.find(p => p.value === selectedOption.value).children);

        options[indexOfSelectedOption].isOpen = true;

        setOptions([...options]);
        setMenuIsOpen(true);
        setShouldRenderSingleValue(!!value);
      } else {
        const valuesOfChildren = selectedOption.children.map(p => p.value);

        const valuesOfChildrenOfChildren = selectedOption.children
          .flatMap(p => p.children)
          .map(p => p?.value)
          .filter(p => !!p);

        const valuesOfOptionsToRemove = [...valuesOfChildren, ...valuesOfChildrenOfChildren];

        const newOptionsToSet = options.filter(p => !valuesOfOptionsToRemove.find(s => s === p.value));
        newOptionsToSet[indexOfSelectedOption].isOpen = false;

        setOptions(newOptionsToSet);
        setMenuIsOpen(true);
        setShouldRenderSingleValue(!!value);
      }
    } else {
      onChange(transformSelectedOption(selectedOption));
      setMenuIsOpen(false);
      setShouldRenderSingleValue(true);
    }
  };

  const toggleMenuIsOpen = (isOpen: boolean) => () => {
    setMenuIsOpen(isOpen);
  };

  return (
    <FormItem
      disabled={disabled}
      fullWidth={inline}
      hint={hint}
      label={label}
      spacingBelow={spacingBelow}
      tooltipDescription={tooltipDescription}
      tooltipHeading={tooltipHeading}
      tooltipWidth={tooltipWidth}
      validationMessage={validationMessage}
      validationState={validationState}
      for={inputId}
    >
      <StyledSelect
        aria-label={ariaLabel}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        className="select-container"
        classNamePrefix="select"
        controlShouldRenderValue={shouldRenderSingleValue}
        defaultMenuIsOpen={defaultMenuOpen}
        defaultValue={defaultValue}
        inputId={inputId}
        isDisabled={disabled}
        isSearchable={false}
        maxMenuHeight={menuHeight}
        menuPlacement={selectMenuPlacement}
        menuIsOpen={menuIsOpen}
        onMenuOpen={toggleMenuIsOpen(true)}
        onMenuClose={toggleMenuIsOpen(false)}
        noOptionsMessage={noOptionsPlaceholder}
        onChange={onChangeHandler}
        onCreateOption={onCreateOption}
        options={options}
        placeholder={placeholder || 'Choose a value'}
        styles={ReactSelectStyles}
        value={value}
        components={{ Option: CustomSelectOption, SingleValue: CustomSelectValue, DropdownIndicator }}
      />
    </FormItem>
  );
};

const DropdownIndicator = props => {
  return (
    <components.DropdownIndicator {...props}>
      <SvgIcon name="angle-down" size="24" />
    </components.DropdownIndicator>
  );
};

const { Option } = components;

const CustomSelectOption = props => {
  return (
    <Option {...props}>
      {props.data.categoryLevel === 1 || props?.data?.children?.length || props?.data?.children?.children?.length ? (
        <ItemWithChildren catergoryLevel={props.data.categoryLevel}>
          {!!props.data.children?.length && (
            <IconContainer>
              {props.data.isOpen ? <SvgIcon name="angle-down" size="24" /> : <SvgIcon name="angle-right" size="24" />}
            </IconContainer>
          )}
          <strong>{props.data.label}</strong>
        </ItemWithChildren>
      ) : (
        <ItemWithoutChildren catergoryLevel={props.data.categoryLevel}>
          <span>{props.data.label}</span>
          {props.isSelected && <SvgIcon name="check" size="24" />}
        </ItemWithoutChildren>
      )}
    </Option>
  );
};

const CustomSelectValue = props => {
  return <SelecetedItem>{props.data.label}</SelecetedItem>;
};
